import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CompanyType } from '@/types/company';
import type { IndustryAppointment, CreateAppointmentInput } from '@/types/industry';
import { useToast } from '@/components/ui/use-toast';

export function useIndustryAppointments(domain: CompanyType) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data } = await supabase.from('companies').select('*').eq('user_id', user!.id).single();
      return data;
    },
  });

  const { data: appointments, isLoading } = useQuery({
    queryKey: ['industry_appointments', company?.id, domain],
    queryFn: async () => {
      if (!company) return [];
      const { data } = await supabase
        .from('industry_appointments')
        .select('*')
        .eq('company_id', company.id)
        .eq('domain', domain)
        .order('appointment_date', { ascending: false });
      return (data || []) as IndustryAppointment[];
    },
    enabled: !!company,
  });

  const createAppointment = useMutation({
    mutationFn: async (input: CreateAppointmentInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data } = await supabase
        .from('industry_appointments')
        .insert({ ...input, domain, company_id: company!.id, user_id: user!.id })
        .select()
        .single();
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industry_appointments'] });
      toast({ title: 'Agendamento criado' });
    },
  });

  return { appointments: appointments || [], isLoading, createAppointment: createAppointment.mutate };
}
